import React, { useState } from 'react';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther } from 'viem';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
    Scale, 
    Clock, 
    CheckCircle, 
    AlertTriangle,
    ExternalLink,
    Loader2,
    Shield,
    Info
} from 'lucide-react';
import { CONTRACT_ABI, CONTRACT_ADDRESS_V2, WETH_ADDRESSES, getExplorerUrl } from '../lib/contract';

// WETH ABI for approval
const WETH_ABI = [
    {
        "type": "function",
        "name": "approve",
        "inputs": [
            { "name": "spender", "type": "address" },
            { "name": "amount", "type": "uint256" }
        ],
        "outputs": [{ "name": "", "type": "bool" }],
        "stateMutability": "nonpayable"
    },
    {
        "type": "function",
        "name": "allowance",
        "inputs": [
            { "name": "owner", "type": "address" },
            { "name": "spender", "type": "address" }
        ],
        "outputs": [{ "name": "", "type": "uint256" }],
        "stateMutability": "view"
    },
    {
        "type": "function",
        "name": "balanceOf",
        "inputs": [{ "name": "account", "type": "address" }],
        "outputs": [{ "name": "", "type": "uint256" }],
        "stateMutability": "view"
    }
];

export function OracleResolutionPanel({ 
    bet, 
    isCreator, 
    isTaker, 
    chainId,
    address 
}) {
    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [step, setStep] = useState('select'); // select, approve, request
    
    const isParticipant = isCreator || isTaker;
    const isActive = bet?.status === 'active';
    const isInAssertion = bet?.status === 'in_assertion';
    
    // Contract write for oracle resolution request
    const { 
        writeContract: requestResolution, 
        data: requestHash,
        isPending: isRequestPending,
        error: requestError
    } = useWriteContract();
    
    const { isLoading: isRequestConfirming, isSuccess: isRequestSuccess } = 
        useWaitForTransactionReceipt({ hash: requestHash });
    
    // Contract write for WETH approval
    const { 
        writeContract: approveWeth, 
        data: approveHash,
        isPending: isApprovePending,
        error: approveError
    } = useWriteContract();
    
    const { isLoading: isApproveConfirming, isSuccess: isApproveSuccess } = 
        useWaitForTransactionReceipt({ hash: approveHash });
    
    // Handle outcome selection
    const handleSelectOutcome = (outcome) => {
        setSelectedOutcome(outcome);
        setStep('approve');
    };
    
    // Handle WETH approval (for bond)
    const handleApproveWeth = async () => {
        const wethAddress = WETH_ADDRESSES[chainId];
        if (!wethAddress) {
            toast.error('WETH not available on this chain');
            return;
        }
        
        try {
            // Approve a reasonable amount for bond (0.01 WETH should be enough)
            await approveWeth({
                address: wethAddress,
                abi: WETH_ABI,
                functionName: 'approve',
                args: [CONTRACT_ADDRESS_V2, parseEther('0.1')]
            });
        } catch (error) {
            console.error('Approval error:', error);
            toast.error('Failed to approve WETH');
        }
    };
    
    // Handle oracle resolution request
    const handleRequestResolution = async () => {
        if (!bet?.on_chain_id || selectedOutcome === null) return;
        
        const creatorWins = selectedOutcome === 'creator';
        
        try {
            await requestResolution({
                address: CONTRACT_ADDRESS_V2,
                abi: CONTRACT_ABI,
                functionName: 'requestOracleResolution',
                args: [BigInt(bet.on_chain_id), creatorWins]
            });
        } catch (error) {
            console.error('Resolution request error:', error);
            toast.error('Failed to request resolution');
        }
    };
    
    // Effect for approval success
    React.useEffect(() => {
        if (isApproveSuccess) {
            toast.success('WETH approved for bond!');
            setStep('request');
        }
    }, [isApproveSuccess]);
    
    // Effect for request success
    React.useEffect(() => {
        if (isRequestSuccess && requestHash) {
            toast.success('Oracle resolution requested!', {
                description: (
                    <a 
                        href={getExplorerUrl(chainId, 'tx', requestHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00FF66] hover:underline flex items-center gap-1"
                    >
                        View transaction <ExternalLink className="w-3 h-3" />
                    </a>
                ),
            });
        }
    }, [isRequestSuccess, requestHash, chainId]);
    
    // Don't show if not eligible
    if (!isParticipant || (!isActive && !isInAssertion)) {
        return null;
    }
    
    // Show assertion status if already in assertion
    if (isInAssertion) {
        return (
            <Card className="bg-[#FFD700]/5 border-[#FFD700]/20">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Scale className="w-5 h-5 text-[#FFD700]" />
                        UMA Oracle Resolution In Progress
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/20">
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-[#FFD700]" />
                            <span className="text-[#FFD700] font-semibold">Challenge Period Active</span>
                        </div>
                        <p className="text-sm text-white/60">
                            An assertion has been made. The 2-hour challenge window is active.
                            If no one disputes, the assertion will be accepted.
                        </p>
                    </div>
                    
                    {bet.assertion_id && (
                        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="text-xs text-white/40 mb-1">Assertion ID</div>
                            <div className="font-mono text-xs text-white/70 break-all">
                                {bet.assertion_id}
                            </div>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-sm text-white/50">
                        <Info className="w-4 h-4" />
                        <span>
                            Asserted outcome: {bet.creator_wins_asserted ? 'Creator wins' : 'Taker wins'}
                        </span>
                    </div>
                    
                    <a 
                        href="https://oracle.uma.xyz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#FFD700] hover:underline"
                    >
                        View on UMA Oracle <ExternalLink className="w-3 h-3" />
                    </a>
                </CardContent>
            </Card>
        );
    }
    
    // Show resolution options for active bets
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Scale className="w-5 h-5 text-[#00FF66]" />
                    Request Oracle Resolution
                </CardTitle>
                <CardDescription className="text-white/50">
                    Use UMA Optimistic Oracle for trustless resolution
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Info Alert */}
                <Alert className="border-[#00FF66]/20 bg-[#00FF66]/5">
                    <Info className="w-4 h-4 text-[#00FF66]" />
                    <AlertDescription className="text-white/60 text-sm">
                        Request decentralized resolution via UMA Oracle. 
                        Requires a small WETH bond (returned if truthful).
                        2-hour challenge period.
                    </AlertDescription>
                </Alert>
                
                {/* Step 1: Select Outcome */}
                {step === 'select' && (
                    <div className="space-y-3">
                        <div className="text-sm text-white/70">Select your assertion:</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                variant="outline"
                                className={`p-4 h-auto flex flex-col items-center gap-2 ${
                                    selectedOutcome === 'creator' 
                                        ? 'border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]' 
                                        : 'border-white/10 text-white/70 hover:border-[#00FF66]/50'
                                }`}
                                onClick={() => handleSelectOutcome('creator')}
                            >
                                <Shield className="w-5 h-5" />
                                <span className="font-semibold">Creator Wins</span>
                            </Button>
                            <Button
                                variant="outline"
                                className={`p-4 h-auto flex flex-col items-center gap-2 ${
                                    selectedOutcome === 'taker' 
                                        ? 'border-[#9B6FE8] bg-[#6F3FCA]/10 text-[#9B6FE8]' 
                                        : 'border-white/10 text-white/70 hover:border-[#6F3FCA]/50'
                                }`}
                                onClick={() => handleSelectOutcome('taker')}
                            >
                                <Shield className="w-5 h-5" />
                                <span className="font-semibold">Taker Wins</span>
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Step 2: Approve WETH */}
                {step === 'approve' && (
                    <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-white/60">Selected assertion:</span>
                                <Badge className={selectedOutcome === 'creator' ? 'bg-[#00FF66]/20 text-[#00FF66]' : 'bg-[#6F3FCA]/20 text-[#9B6FE8]'}>
                                    {selectedOutcome === 'creator' ? 'Creator Wins' : 'Taker Wins'}
                                </Badge>
                            </div>
                        </div>
                        
                        <div className="text-sm text-white/70">
                            Step 1: Approve WETH for bond deposit
                        </div>
                        
                        {approveHash && (
                            <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                                <div className="text-xs text-[#00FF66] mb-1">Approval Transaction</div>
                                <a 
                                    href={getExplorerUrl(chainId, 'tx', approveHash)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1"
                                >
                                    {approveHash.slice(0, 10)}...{approveHash.slice(-8)}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        )}
                        
                        {approveError && (
                            <Alert className="border-red-500/30 bg-red-500/10">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <AlertDescription className="text-red-400 text-sm">
                                    {approveError.shortMessage || 'Approval failed'}
                                </AlertDescription>
                            </Alert>
                        )}
                        
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 border-white/10"
                                onClick={() => {
                                    setStep('select');
                                    setSelectedOutcome(null);
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                className="flex-1 bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                                onClick={handleApproveWeth}
                                disabled={isApprovePending || isApproveConfirming}
                            >
                                {isApprovePending ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirm...</>
                                ) : isApproveConfirming ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Approving...</>
                                ) : (
                                    'Approve WETH'
                                )}
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Step 3: Request Resolution */}
                {step === 'request' && (
                    <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-[#00FF66] font-semibold">WETH Approved</span>
                            </div>
                            <p className="text-sm text-white/60">
                                Ready to submit your assertion to UMA Oracle
                            </p>
                        </div>
                        
                        <div className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                            <div className="text-sm text-white/60 mb-1">Your assertion:</div>
                            <div className="font-semibold text-white">
                                {selectedOutcome === 'creator' ? 'Creator wins this bet' : 'Taker wins this bet'}
                            </div>
                        </div>
                        
                        {requestHash && (
                            <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                                <div className="text-xs text-[#00FF66] mb-1">Request Transaction</div>
                                <a 
                                    href={getExplorerUrl(chainId, 'tx', requestHash)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1"
                                >
                                    {requestHash.slice(0, 10)}...{requestHash.slice(-8)}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        )}
                        
                        {requestError && (
                            <Alert className="border-red-500/30 bg-red-500/10">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <AlertDescription className="text-red-400 text-sm">
                                    {requestError.shortMessage || 'Request failed. You may need WETH for the bond.'}
                                </AlertDescription>
                            </Alert>
                        )}
                        
                        <Button
                            className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                            onClick={handleRequestResolution}
                            disabled={isRequestPending || isRequestConfirming || isRequestSuccess}
                        >
                            {isRequestPending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirm in Wallet...</>
                            ) : isRequestConfirming ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                            ) : isRequestSuccess ? (
                                <><CheckCircle className="w-4 h-4 mr-2" />Assertion Submitted!</>
                            ) : (
                                <><Scale className="w-4 h-4 mr-2" />Submit to UMA Oracle</>
                            )}
                        </Button>
                        
                        <p className="text-xs text-white/40 text-center">
                            After submission, there's a 2-hour challenge period.
                            If undisputed, the bet will be resolved automatically.
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
